import React, { useCallback, useMemo, useState } from 'react';
import clsx from 'clsx';

import { useStyles } from './style';
import {
  Grid,
  Typography,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Box,
} from '@material-ui/core';
import { ThemedButton } from '../../../../shared/components/themed-button';
import CheckIcon from '@material-ui/icons/Check';
import { NavLink, Route, Switch, useHistory, useRouteMatch } from 'react-router-dom';
import { RestaurantUser, SupplierUser } from '../../../../api/user/types';
import { OverlayModal } from '../../../../shared/components/overlay-modal';
import { SupplierPayment } from '../SupplierPayment';
import { SupplierTransactions } from '../SupplierTransactions';
import { PlanTypes } from '../CurrentPlan';
import { SupplierStripe } from '../SupplierStripe';
import { PipedriveScheduler } from '../PipedriveScheduler';
import { useScreenSize } from '../../../../shared/hooks/use-screen-size';
import { useGetStripeProductsQuery } from '../../../../api/stripe-product';
import { LocalStorageService } from '../../../../shared/services/localStorage.service';
import { formatCurrency } from '../../../../shared/helpers/format-currency';

interface Props {
  upgradePlan: () => void;
  currentPlan: PlanTypes;
  currentUser: RestaurantUser | SupplierUser | undefined;
}

export const YourPlan: React.FC<Props> = ({ upgradePlan, currentPlan, currentUser }) => {
  const classes = useStyles();
  const { push } = useHistory();
  const { isMobile } = useScreenSize();
  const [isOpenScheduleModal, setIsOpenScheduleModal] = useState(false);
  const matchPlansRoot = useRouteMatch({ path: '/account/billing/plans', exact: true });
  const countryCode = LocalStorageService.getItem('country') || 'au';
  const { data: products } = useGetStripeProductsQuery({country_code: countryCode});

  const isLastPaymentFailed = useMemo(() => {
    return currentUser?.has_payment_error === true;
  }, [currentUser]);

  const productPrice = useCallback((interval: string, subType: string) => {
    const product = products?.stripe_products?.find(
      (p) => p.product_key === subType
    );

    if(!product) {
      return 0;
    }

    const price = product.stripe_prices.find(
      (p) => p.interval === interval
    );

    if(!price) {
      return 0;
    }

    return price.unit_amount;
  }, [products]);

  const isUnlimitedPlan = useMemo(() => {
    return currentPlan === 'unlimited';
  }, [currentPlan]);

  const isProLitePlan = useMemo(() => {
    return currentPlan === 'pro_lite';
  }, [currentPlan]);

  const isFreePlan = useMemo(() => {
    return currentPlan === 'free';
  }, [currentPlan]);

  const isEnterprisePlan = useMemo(() => {
    return currentPlan === 'enterprise';
  }, [currentPlan]);

  const isTrialPlan = useMemo(() => {
    return currentPlan === 'trial';
  }, [currentPlan]);

  const trailText = useMemo(() => {
    if(!isTrialPlan || !currentUser) {
      return '';
    }
    if(currentUser.trial_days_left === 1) {
      return '1 day left of Trial';
    } else {
      return `${currentUser.trial_days_left} days left of Trial`;
    }
  }, [isTrialPlan, currentUser]);

  const nextPaymentText = useMemo(() => {
    if(!currentUser) {
      return '';
    }

    if(isProLitePlan || isUnlimitedPlan || isFreePlan || isEnterprisePlan) {
      const priceText = `${formatCurrency(productPrice(currentUser.subscription_interval, currentUser.subscription_type) / 100, 0, products?.price_country_code, products?.price_country_currency)}`;
      if(currentUser.subscription_renewal_date) {
        return `${priceText} on ${currentUser.subscription_renewal_date}`;  
      }
  
      return priceText;
    }

    return '';
  }, [isUnlimitedPlan, isProLitePlan, isFreePlan, isEnterprisePlan, currentUser, productPrice, products]);

  const goToPaymentMethod = useCallback(() => {
      push('/account/billing/plans/payment');
  }, []);

  return (
    <Box className={classes.root}>
       <Typography className={classes.header}>
        {
          isFreePlan
            ? 'Billing - Plans'
            : 'Billing'
        }
      </Typography>
      {(isFreePlan || isTrialPlan) && <Typography variant="subtitle1" className={classes.subHeader}>
        Reduce admin and fulfil more orders faster with Pro
      </Typography>}

      {(isUnlimitedPlan || isProLitePlan || isEnterprisePlan) && <div className={clsx(classes.tabs)}>
        <NavLink exact to='/account/billing/plans' className={classes.tabLink} activeClassName={classes.tabLinkActive}>
          Subscriptions
        </NavLink>
        <NavLink
          to='/account/billing/plans/stripe'
          className={classes.tabLink}
          activeClassName={classes.tabLinkActive}>
          Stripe
        </NavLink>
        <NavLink
          to='/account/billing/plans/payment'
          className={classes.tabLink}
          activeClassName={classes.tabLinkActive}>
          Payment Method
        </NavLink>
        <NavLink
          to='/account/billing/plans/transactions'
          className={classes.tabLink}
          activeClassName={classes.tabLinkActive}
        >
          Transactions
        </NavLink>
      </div>}

      <Switch>
        <Route path='/account/billing/plans/stripe' component={SupplierStripe} />
        <Route path='/account/billing/plans/payment' component={SupplierPayment} />
        <Route path='/account/billing/plans/transactions' component={SupplierTransactions} />
      </Switch>

      {matchPlansRoot && <>
        <Grid container spacing={4} >
          {isFreePlan && <Grid item xs={12} md={6} className={classes.cardBox}>
            <Card className={clsx(classes.card, classes.yourPlanCard)}>
              <CardContent className={classes.cardContent}>
                <Box className={classes.titleContainer}>
                  <Box className={classes.planTitleHeader}>
                    <Typography className={classes.planLabel}>
                        YOUR PLAN
                    </Typography>
                  </Box>
                  <Typography variant="h4" component="h2" className={classes.mainPlanTitle} gutterBottom>
                    Free Plan
                  </Typography>
                </Box>
                <Box className={classes.bodyContainer}>
                  <Typography variant="h6" className={classes.cardFeatureTitle} gutterBottom>
                    Upgrade to Pro to Save Time & Reduce Admin
                  </Typography>
                  <Typography variant="body2" className={classes.cardText} paragraph>
                    The Pro Plan automates your workflow so you can focus on fulfilling orders
                  </Typography>
                  <Box className={classes.keepFeaturesContainer}>
                    <ThemedButton
                      onClick={upgradePlan}
                      title='Upgrade Plan'
                      buttonStyle='blueDarkButton'
                      width={340}
                      height={60}
                      fontSize={20}
                      customClass={classes.ctaButton}
                    />
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>}

          {isTrialPlan && <Grid item xs={12} md={6} className={classes.cardBox}>
            <Card className={clsx(classes.card, classes.yourPlanCard)}>
              <CardContent className={classes.cardContent}>
                <Box className={classes.titleContainer}>
                  <Box className={classes.planTitleHeader}>
                    <Typography className={classes.planLabel}>
                        YOUR PLAN
                    </Typography>
                    <Typography className={classes.trialBadge}>{trailText}</Typography>
                  </Box>
                  <Typography variant="h4" component="h2" className={classes.mainPlanTitle} gutterBottom>
                    Free Trial
                  </Typography>
                </Box>
                <Box className={classes.bodyContainer}>
                  <Typography variant="h6" className={classes.cardFeatureTitle} gutterBottom>
                    Liking the features?
                  </Typography>
                  <Typography variant="body2" className={classes.cardText} paragraph>
                    Keep uninterrupted access to inventory, automation, and accounting tools.
                  </Typography>
                  <Typography variant="h6" className={classes.upgradeText}>
                      Upgrade Plan to Keep These Features
                  </Typography>
                  <Box className={classes.keepFeaturesContainer}>
                    <ThemedButton
                      onClick={upgradePlan}
                      title='Upgrade Plan'
                      buttonStyle='blueDarkButton'
                      width={340}
                      height={60}
                      fontSize={20}
                      customClass={classes.ctaButton}
                    />
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>}

          {(isUnlimitedPlan || isProLitePlan) && <Grid item xs={12} md={6} className={classes.cardBox}>
            <Card className={clsx(classes.card, classes.yourPlanCard)}>
              <CardContent className={classes.cardContent}>
                <Box className={classes.titleContainer}>
                  <Box className={classes.planTitleHeader}>
                    <Typography className={classes.planLabel}>
                        YOUR PLAN
                    </Typography>
                  </Box>
                  <Typography variant="h4" component="h2" className={classes.mainPlanTitle} gutterBottom>
                    {
                      isProLitePlan
                        ? 'Pro - Lite Plan'
                        : 'Pro - Standard Plan'
                    }
                  </Typography>
                  <Typography variant="h4" component="h2" className={classes.mainPlanSubTitle} gutterBottom>
                  {
                      isProLitePlan
                        ? '(up to 100 orders)'
                        : '(up to 500 orders)'
                    }
                  </Typography>
                </Box>
                {
                  isLastPaymentFailed
                    ? <Box className={clsx(classes.bodyContainer, classes.paymentFailedContainer)}>
                        <Typography className={clsx(classes.nextPaymentText, classes.paymentFailedText)} gutterBottom>
                          <strong>Your last payment failed</strong><br />Please check your payment method 
                        </Typography>
                        <Typography className={clsx(classes.changePlanText, classes.paymentFailedBtns)}>
                          <span onClick={upgradePlan}>Change Plan</span><span onClick={goToPaymentMethod}>Check Payment Method</span>
                        </Typography>
                      </Box>
                    : <Box className={clsx(classes.bodyContainer, classes.nextPaymentContainer)}>
                        <Typography className={classes.nextPaymentText} gutterBottom>
                          Next payment: <strong>{nextPaymentText}</strong>
                        </Typography>
                        <Typography className={classes.changePlanText} onClick={upgradePlan}>
                          Change Plan
                        </Typography>
                      </Box>}
              </CardContent>
            </Card>
          </Grid>}

          {(isUnlimitedPlan || isProLitePlan) && <Grid item xs={12} md={6} className={classes.unlimitedBox}>
            <Card className={clsx(classes.card)}>
              <CardContent className={classes.cardContent}>
                <Box className={clsx(classes.bodyContainer, classes.unlockEnterpriseContainer)}>
                  <Typography variant="h6" className={classes.cardFeatureTitle} gutterBottom>
                    Unlock Enterprise-Grade Tools Built for Scale
                  </Typography>
                  <Typography variant="body2" className={clsx(classes.cardText, classes.unlockEnterpriseText)} paragraph>
                    Designed for suppliers with custom workflows, advanced integrations, and a need for full control
                  </Typography>
                  <Box className={classes.keepFeaturesContainer}>
                    <ThemedButton
                      onClick={() => setIsOpenScheduleModal(true)}
                      title='Schedule a call'
                      buttonStyle='blueDarkButton'
                      width={340}
                      height={60}
                      fontSize={20}
                      customClass={classes.ctaButton}
                    />
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>}

          {(isFreePlan || isTrialPlan) &&<Grid item xs={12} md={6} className={classes.unlimitedBox}>
            <Card className={clsx(classes.card, classes.unlimitedCard)}>
              <CardContent className={classes.cardContent}>
                <Box className={classes.unlimitedUpper}>
                  <Typography variant="h6" component="h3" className={classes.unlimitedCardFeatureTitle}>
                    Here's what's included in the Pro Plan
                  </Typography>
                  <Typography className={classes.recommendedText}>
                    (Recommended)
                  </Typography>
                </Box>
                <List dense className={classes.listItemWrapper}>
                  <ListItem disableGutters className={classes.listItem}>
                    <ListItemIcon className={classes.listItemIcon}>
                      <CheckIcon className={classes.checkIcon} />
                    </ListItemIcon>
                    <ListItemText primary="Grow faster with automation of inventory, pricing, and accounting" className={classes.listItemText}/>
                  </ListItem>
                  <ListItem disableGutters className={classes.listItem}>
                    <ListItemIcon className={classes.listItemIcon}>
                      <CheckIcon className={classes.checkIcon} />
                    </ListItemIcon>
                    <ListItemText primary="Reduce order processing costs by up to 95%" className={classes.listItemText}/>
                  </ListItem>
                  <ListItem disableGutters className={classes.listItem}>
                    <ListItemIcon className={classes.listItemIcon}>
                      <CheckIcon className={classes.checkIcon} />
                    </ListItemIcon>
                    <ListItemText primary="Cut picking errors by 80% with smart digital fulfilment tools" className={classes.listItemText}/>
                  </ListItem>
                  <ListItem disableGutters className={classes.listItem}>
                    <ListItemIcon className={classes.listItemIcon}>
                      <CheckIcon className={classes.checkIcon} />
                    </ListItemIcon>
                    <ListItemText primary="Gain real-time insights and analytics" className={classes.listItemText}/>
                  </ListItem>
                  <ListItem disableGutters className={classes.listItem}>
                    <ListItemIcon className={classes.listItemIcon}>
                      <CheckIcon className={classes.checkIcon} />
                    </ListItemIcon>
                    <ListItemText primary="Integrate smoothly with your accounting systems like Xero, Quickbooks and SAP" className={classes.listItemText}/>
                  </ListItem>
                  <ListItem disableGutters className={classes.listItem}>
                    <ListItemIcon className={classes.listItemIcon}>
                      <CheckIcon className={classes.checkIcon} />
                    </ListItemIcon>
                    <ListItemText primary="Ideal for growing suppliers looking to scale" className={classes.listItemText}/>
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>}

          {isEnterprisePlan && <Grid item xs={12} md={6} className={classes.cardBox}>
            <Card className={clsx(classes.card, classes.yourPlanCard)}>
              <CardContent className={classes.cardContent}>
                <Box className={classes.titleContainer}>
                  <Box className={classes.planTitleHeader}>
                    <Typography className={classes.planLabel}>
                        YOUR PLAN
                    </Typography>
                  </Box>
                  <Typography variant="h4" component="h2" className={clsx(classes.mainPlanTitle, classes.enterpriseTitle)} gutterBottom>
                    Enterprise / Custom
                  </Typography>
                </Box>
                {
                  isLastPaymentFailed
                    ? <Box className={clsx(classes.bodyContainer, classes.paymentFailedContainer)}>
                        <Typography className={clsx(classes.nextPaymentText, classes.paymentFailedText)} gutterBottom>
                          <strong>Your last payment failed</strong><br />Please check your payment method 
                        </Typography>
                        <Typography className={clsx(classes.changePlanText, classes.paymentFailedBtns)}>
                          <span onClick={upgradePlan}>Change Plan</span><span onClick={goToPaymentMethod}>Check Payment Method</span>
                        </Typography>
                      </Box>
                    : <Box className={clsx(classes.bodyContainer, classes.nextPaymentContainer)}>
                        <Typography className={classes.nextPaymentText} gutterBottom>
                          Next payment: <strong>{nextPaymentText}</strong>
                        </Typography>
                        <Typography className={classes.changePlanText} onClick={upgradePlan}>
                          Change Plan
                        </Typography>
                      </Box>}
              </CardContent>
            </Card>
          </Grid>}

          {isEnterprisePlan &&<Grid item xs={12} md={6} className={classes.unlimitedBox}>
            <Card className={clsx(classes.card, classes.unlimitedCard)}>
              <CardContent className={classes.cardContent}>
                <Box className={classes.unlimitedUpper}>
                  <Typography variant="h6" component="h3" className={classes.unlimitedCardFeatureTitle}>
                    Here's what's included in the <strong>Enterprise Plan</strong>
                  </Typography>
                </Box>
                <List dense className={classes.listItemWrapper}>
                  <ListItem disableGutters className={classes.listItem}>
                    <ListItemIcon className={classes.listItemIcon}>
                      <CheckIcon className={classes.checkIcon} />
                    </ListItemIcon>
                    <ListItemText primary="Fully customised ordering app and white-label online store" className={classes.listItemText}/>
                  </ListItem>
                  <ListItem disableGutters className={classes.listItem}>
                    <ListItemIcon className={classes.listItemIcon}>
                      <CheckIcon className={classes.checkIcon} />
                    </ListItemIcon>
                    <ListItemText primary="ERP integrations tailored exactly to your business needs" className={classes.listItemText}/>
                  </ListItem>
                  <ListItem disableGutters className={classes.listItem}>
                    <ListItemIcon className={classes.listItemIcon}>
                      <CheckIcon className={classes.checkIcon} />
                    </ListItemIcon>
                    <ListItemText primary="Automate manual orders from Email, SMS & WhatsApp using advanced AI (coming soon)" className={classes.listItemText}/>
                  </ListItem>
                  <ListItem disableGutters className={classes.listItem}>
                    <ListItemIcon className={classes.listItemIcon}>
                      <CheckIcon className={classes.checkIcon} />
                    </ListItemIcon>
                    <ListItemText primary="Personalised onboarding, dedicated support & priority service" className={classes.listItemText}/>
                  </ListItem>
                  <ListItem disableGutters className={classes.listItem}>
                    <ListItemIcon className={classes.listItemIcon}>
                      <CheckIcon className={classes.checkIcon} />
                    </ListItemIcon>
                    <ListItemText primary="Perfect for established suppliers with custom requirements" className={classes.listItemText}/>
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>}
        </Grid>
        <OverlayModal closeOnClickOutside={true} showClose={isMobile ? false : true} boxClassName={classes.modalBox} isOpen={isOpenScheduleModal} onClose={() => setIsOpenScheduleModal(false)}>
          <PipedriveScheduler />
        </OverlayModal>
      </>}
    </Box>
)};
